import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Briefcase, Users, TrendingUp, Clock, Bell, FileText, 
  Calendar, Search, Plus, Edit2, Trash2, Eye, Mail, Phone 
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import BlogManagementModal from '@/components/BlogManagementModal';
import JobManagementModal from '@/components/JobManagementModal';
import UserManagementTable from '@/components/UserManagementTable';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


export default function Dashboard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await api.getMe();
      if (userData.role !== 'admin') {
        toast.error('Admin access required');
        navigate('/');
        return;
      }
      setUser(userData);
      fetchData();
    } catch (err) {
      toast.error('Please sign in');
      navigate('/login');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [blogsData, jobsData, appsData] = await Promise.all([
        api.getBlogs(),
        api.getJobs(),
        api.getApplications()
      ]);
      setBlogs(blogsData);
      setJobs(jobsData);
      setApplications(appsData);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = () => {
    setSelectedBlog(null);
    setShowBlogModal(true);
  };

  const handleEditBlog = (blog) => {
    setSelectedBlog(blog);
    setShowBlogModal(true);
  };

  const handleDeleteBlog = async () => {
    if (!blogToDelete) return;
    try {
      await api.deleteBlog(blogToDelete.id);
      toast.success('Blog post deleted successfully');
      setBlogToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete blog post');
    }
  };

  const handleDeleteJob = async () => {
    try {
      await api.deleteJob(jobToDelete);
      toast.success('Job post deleted successfully');
      setJobToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete job');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };
  
  const stats = [
    { label: 'Active Job Postings', value: jobs.length.toString(), icon: Briefcase, change: '+3 this week' },
    { label: 'Total Applications', value: applications.length.toString(), icon: Users, change: '+28 this month' },
    { label: 'Blog Posts', value: blogs.length.toString(), icon: FileText, change: 'Published' },
    { label: 'Pending Reviews', value: applications.filter(a => a.status === 'submitted').length.toString(), icon: Clock, change: 'Today' }
  ];
  
  const recentActivity = [
    { action: 'New candidate application', role: 'Senior Talent Consultant', time: '2 hours ago' },
    { action: 'Interview scheduled', role: 'Recruitment Coordinator', time: '4 hours ago' },
    { action: 'Offer accepted', role: 'Business Development Manager', time: '1 day ago' },
    { action: 'New job posting created', role: 'Workforce Strategy Analyst', time: '2 days ago' },
    { action: 'Candidate profile updated', role: 'Talent Acquisition Specialist', time: '3 days ago' }
  ];
  
  const upcomingInterviews = [
    { candidate: 'Sarah Martinez', role: 'Senior Consultant', time: 'Today, 2:00 PM' },
    { candidate: 'James Wilson', role: 'Recruitment Coordinator', time: 'Tomorrow, 10:00 AM' },
    { candidate: 'Emily Chen', role: 'Business Development', time: 'Tomorrow, 3:00 PM' }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">
                  Dashboard
                </h1>
                <p className="text-gray-600">Welcome back{user ? `, ${user.full_name || user.email}` : ''}! Here's what's happening with your talent pipeline.</p>
              </div>
              <div className="w-full md:w-80">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search candidates, jobs..."
                    className="pl-10 h-11"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="dashboard-search-input"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-anim="list-stagger" data-stagger="80">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card key={index} className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="text-blue-600" size={24} />
                      </div>
                      <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                        {stat.change}
                      </span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Tabs for Content Management */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
              <TabsTrigger value="blogs" data-testid="blogs-tab">Blog Management</TabsTrigger>
              <TabsTrigger value="jobs" data-testid="jobs-tab">Job Postings</TabsTrigger>
              <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>
              <TabsTrigger value="users" data-testid="users-tab">User Management</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Activity */}
                <div className="lg:col-span-2">
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                        <Button variant="ghost" size="sm" className="text-blue-600">
                          View All
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {recentActivity
                          .filter(activity => 
                            !searchQuery || 
                            activity.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            activity.role.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map((activity, index) => (
                          <div key={index} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                              <Bell className="text-blue-600" size={18} />
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-900 font-medium">{activity.action}</p>
                              <p className="text-sm text-gray-600">{activity.role}</p>
                              <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Quick Actions */}
                  <Card className="border-none shadow-md mt-8">
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Button 
                          onClick={handleCreateBlog}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-4 flex-col"
                          data-testid="quick-create-blog"
                        >
                          <FileText className="mb-2" size={24} />
                          <span>Create Blog Post</span>
                        </Button>
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-4 flex-col">
                          <Users className="mb-2" size={24} />
                          <span>Browse Applications</span>
                        </Button>
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-4 flex-col">
                          <Calendar className="mb-2" size={24} />
                          <span>Schedule Interview</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Sidebar */}
                <div className="space-y-8">
                  {/* Upcoming Interviews */}
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">Upcoming Interviews</h2>
                      <div className="space-y-4">
                        {upcomingInterviews.map((interview, index) => (
                          <div key={index} className="pb-4 border-b border-gray-100 last:border-0">
                            <p className="font-medium text-gray-900">{interview.candidate}</p>
                            <p className="text-sm text-gray-600 mt-1">{interview.role}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              <Clock size={14} />
                              <span>{interview.time}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" className="w-full mt-4">
                        View Calendar
                      </Button>
                    </CardContent>
                  </Card>
                  
                  {/* Performance */}
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-6">This Month</h2>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Placement Goal</span>
                            <span className="font-medium text-gray-900">85%</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Candidate Engagement</span>
                            <span className="font-medium text-gray-900">92%</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-green-600 rounded-full" style={{ width: '92%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Client Satisfaction</span>
                            <span className="font-medium text-gray-900">97%</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-600 rounded-full" style={{ width: '97%' }}></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Blog Management Tab */}
            <TabsContent value="blogs">
              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Blog Posts</h2>
                    <Button 
                      onClick={handleCreateBlog}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="create-blog-button"
                    >
                      <Plus className="mr-2" size={18} />
                      Create Blog Post
                    </Button>
                  </div>

                  {loading ? (
                    <p className="text-gray-600">Loading blogs...</p>
                  ) : blogs.length === 0 ? (
                    <p className="text-gray-600">No blog posts yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-4">
                      {blogs.map((blog) => (
                        <Card key={blog.id} className="border border-gray-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{blog.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">{blog.excerpt}</p>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span>{blog.category || 'Uncategorized'}</span>
                                  <span>•</span>
                                  <span>{blog.author || 'Anonymous'}</span>
                                  <span>•</span>
                                  <span>{formatDate(blog.created_at)}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditBlog(blog)}
                                  data-testid={`edit-blog-${blog.id}`}
                                >
                                  <Edit2 size={16} />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setBlogToDelete(blog)}
                                  className="text-red-600 hover:text-red-700"
                                  data-testid={`delete-blog-${blog.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Job Postings Tab */}
            <TabsContent value="jobs">
              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Job Postings</h2>
                    <Button
                      onClick={() => {
                        setJobToEdit(null);
                        setShowJobModal(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="create-job-button"
                    >
                      <Plus className="mr-2" size={18} />
                      Post New Job
                    </Button>
                  </div>
                  {loading ? (
                    <p className="text-gray-600">Loading jobs...</p>
                  ) : jobs.length === 0 ? (
                    <p className="text-gray-600">No job postings yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {jobs.map((job) => (
                        <Card key={job.id} className="border border-gray-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{job.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">{job.description}</p>
                                <div className="flex items-center gap-4 text-xs text-gray-500 mb-1">
                                  <span>{job.department}</span>
                                  <span>•</span>
                                  <span>{job.location}</span>
                                  <span>•</span>
                                  <span>{job.type}</span>
                                </div>
                                {job.salary_range && (
                                  <div className="text-xs text-gray-500">Salary: {job.salary_range}</div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setJobToEdit(job);
                                    setShowJobModal(true);
                                  }}
                                  data-testid={`edit-job-${job.id}`}
                                >
                                  <Edit2 size={16} />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setJobToDelete(job.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-job-${job.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Job Applications</h2>
                    <div className="text-sm text-gray-600">
                      Total: <span className="font-semibold text-blue-600">{applications.length}</span> application{applications.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {loading ? (
                    <p className="text-gray-600">Loading applications...</p>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-3">
                        <FileText size={48} className="mx-auto" />
                      </div>
                      <p className="text-gray-600 font-medium">No applications yet</p>
                      <p className="text-sm text-gray-500 mt-2">Applications will appear here when candidates apply for your job postings.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app, index) => (
                        <Card key={app.id} className="border border-gray-200 hover:shadow-md transition-all" data-anim="fade-up" data-delay={index * 50}>
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-6">
                              <div className="flex-1">
                                {/* Applicant Info */}
                                <div className="flex items-start gap-4 mb-4">
                                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {app.name ? app.name.charAt(0).toUpperCase() : 'A'}
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-xl font-semibold text-gray-900 mb-1">{app.name || 'Anonymous'}</h3>
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <Mail size={14} />
                                        {app.email || 'No email'}
                                      </span>
                                      {app.phone && (
                                        <>
                                          <span>•</span>
                                          <span className="flex items-center gap-1">
                                            <Phone size={14} />
                                            {app.phone}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Job Info */}
                                {app.job_title && (
                                  <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-500 uppercase">Applied for:</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">{app.job_title}</p>
                                  </div>
                                )}

                                {/* Cover Letter Preview */}
                                {app.cover_letter && (
                                  <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-500 uppercase">Cover Letter:</span>
                                    <p className="text-sm text-gray-700 mt-1 line-clamp-2">{app.cover_letter}</p>
                                  </div>
                                )}

                                {/* Status & Date */}
                                <div className="flex items-center gap-4 text-xs mt-3 pt-3 border-t">
                                  <span className="flex items-center gap-1">
                                    <span className="font-medium text-gray-500">Status:</span>
                                    <span className={`px-2 py-1 rounded-full font-semibold ${
                                      app.status === 'reviewed' ? 'bg-green-100 text-green-700' :
                                      app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {app.status || 'New'}
                                    </span>
                                  </span>
                                  <span>•</span>
                                  <span className="text-gray-500">
                                    Applied {formatDate(app.applied_at || app.created_at)}
                                  </span>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex flex-col gap-2">
                                {app.resume_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(app.resume_url, '_blank')}
                                    className="whitespace-nowrap"
                                  >
                                    <Eye size={16} className="mr-2" />
                                    View Resume
                                  </Button>
                                )}
                                {app.email && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.location.href = `mailto:${app.email}`}
                                    className="whitespace-nowrap"
                                  >
                                    <Mail size={16} className="mr-2" />
                                    Contact
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users">
              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">User Management</h2>
                  {loading ? (
                    <p className="text-gray-600">Loading users...</p>
                  ) : (
                    <UserManagementTable />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Blog Management Modal */}
      <BlogManagementModal
        isOpen={showBlogModal}
        onClose={() => {
          setShowBlogModal(false);
          setSelectedBlog(null);
        }}
        blog={selectedBlog}
        onSuccess={fetchData}
      />

      {/* Job Management Modal */}
      <JobManagementModal
        isOpen={showJobModal}
        onClose={() => {
          setShowJobModal(false);
          setJobToEdit(null);
        }}
        job={jobToEdit}
        onSuccess={fetchData}
      />

      {/* Delete Blog Confirmation Dialog */}
      <AlertDialog open={!!blogToDelete} onOpenChange={() => setBlogToDelete(null)}>
        <AlertDialogContent data-testid="delete-blog-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this blog post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBlog}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Job Confirmation Dialog */}
      <AlertDialog open={!!jobToDelete} onOpenChange={() => setJobToDelete(null)}>
        <AlertDialogContent data-testid="delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job Posting</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job posting? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Footer />
    </div>
  );
}
